"use client";

import { Feld, type FeldEigenschaften } from "./feld";
import { TAG_PUNKT, type TagFarbe } from "./auswahl-konfiguration";

/**
 * Farbwahl eines Tags (Paket Tags T-b, Musterkatalog M-041, Design-Guide `formularfelder.md`, Betreiber 13.10.2026 V2 A): eine
 * Gruppe aus bis zu fünf Wahlchips (Radio), je mit Farbpunkt und Name, die gewählte mit Rahmen und Haken. Die Farben sind die
 * vier festen Tag-Tokens (primär, grün, orange, grau; AW-006) plus „Automatisch“ (kein eigener Farbton, Farbpunkt als leerer Ring:
 * die Farbe ergibt sich aus dem Namen). Der Haken ist nie das einzige Signal: der Name steht im Chip, der Punkt ist nur Marker.
 *
 * Der Baustein kennt keine Speicherwerte: der Aufrufer liefert `optionen` (Wert, Name, Farbe) und hält die Wahl (kontrolliert).
 * Bedienung: native Radios (`sr-only`), Pfeiltasten wechseln innerhalb der Gruppe, der Fokus zeigt sich als Ring am Chip.
 */
export interface TagFarbwahlOption {
  wert: string;
  label: string;
  /** Fehlt sie, zeigt der Chip den Ring „Automatisch“ statt eines Farbpunkts. */
  farbe?: TagFarbe;
}

const CHIP =
  "relative inline-flex items-center gap-x-2 py-1.5 px-3 min-h-9 text-sm text-foreground bg-layer rounded-lg cursor-pointer ring-1 ring-layer-line has-checked:ring-2 has-checked:ring-primary-checked has-focus-visible:ring-2 has-focus-visible:ring-primary-focus has-disabled:opacity-50 has-disabled:cursor-default";
const PUNKT_AUTOMATISCH = "inline-block size-2.5 rounded-full border border-line-3";

export function TagFarbwahl({
  name,
  optionen,
  wert,
  beiAenderung,
  deaktiviert = false,
  ...feld
}: Omit<FeldEigenschaften, "layout" | "groesse"> & {
  name: string;
  optionen: readonly TagFarbwahlOption[];
  wert: string;
  beiAenderung: (neu: string) => void;
  deaktiviert?: boolean;
}) {
  return (
    <>
      {/* Preline: ui-components/ui-components-sub-menus-selection/ui-components-cat-selection-menus/status-menu-with-color-swatches (nur die Anordnung der Farbpunkte; Farben aus den Theme-Tokens) */}
      {/* Abweichung AW-061: Farbwahl als Wahlchips mit Punkt und Namen, kein Katalogvorbild mit Theme-Farben (Pro-Block nutzt die Roh-Palette) */}
      <Feld {...feld} gruppe>
        {(a) => (
          <div role="radiogroup" aria-labelledby={a.labelId} aria-describedby={a["aria-describedby"]} className="flex flex-wrap gap-2">
            {optionen.map((o) => {
              const optionId = `${a.id}-${o.wert}`;
              const gewaehlt = wert === o.wert;
              return (
                <label key={o.wert} htmlFor={optionId} className={CHIP}>
                  <input
                    id={optionId}
                    type="radio"
                    name={name}
                    value={o.wert}
                    className="sr-only"
                    checked={gewaehlt}
                    disabled={deaktiviert}
                    onChange={() => beiAenderung(o.wert)}
                  />
                  <span className={o.farbe ? TAG_PUNKT[o.farbe] : PUNKT_AUTOMATISCH} aria-hidden="true" />
                  {o.label}
                  {gewaehlt && (
                    <svg className="shrink-0 size-4 text-primary" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                  )}
                </label>
              );
            })}
          </div>
        )}
      </Feld>
    </>
  );
}
